import { useCallback, type FunctionComponent, type MouseEvent } from 'react';
import { useIntl } from 'react-intl';

import PinStrikethroughIcon from '@atlaskit/icon-lab/core/pin-strikethrough';
import PinIcon from '@atlaskit/icon/core/pin';
import { boardSwitcherSharedState } from '@trello/board-switcher';
import { Button } from '@trello/nachos/button';
import { Tooltip } from '@trello/nachos/tooltip';
import { useSharedStateSelector } from '@trello/shared-state';
import { useSplitScreenSharedState } from '@trello/split-screen';

import * as styles from './TogglePanelButton.module.less';

export const TogglePanelButton: FunctionComponent = () => {
  const intl = useIntl();

  const currentMode = useSharedStateSelector(
    boardSwitcherSharedState,
    useCallback((state) => state.mode, []),
  );

  const { configuration: splitScreenConfiguration } =
    useSplitScreenSharedState();

  const modalLabel = intl.formatMessage({
    id: 'templates.board_switcher.pin-switcher-as-a-sidebar',
    defaultMessage: 'Pin switcher as a sidebar',
    description: 'Button label for pinning the board switcher as a sidebar',
  });

  const panelLabel = intl.formatMessage({
    id: 'templates.board_switcher.unpin-switcher',
    defaultMessage: 'Unpin switcher',
    description: 'Button label for unpinning the board switcher',
  });

  const label = currentMode === 'panel' ? panelLabel : modalLabel;

  const onChange = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const mode = currentMode === 'panel' ? 'modal' : 'panel';
      const layout = mode === 'panel' ? 'list' : 'grid';

      // TODO: Add analytics

      boardSwitcherSharedState.setValue({
        mode,
        layout,
      });
    },
    [currentMode],
  );

  // Don't render the settings button on small screens
  if (splitScreenConfiguration === 'tabs') {
    return null;
  }

  const icon =
    currentMode === 'panel' ? (
      <PinStrikethroughIcon label="" />
    ) : (
      <PinIcon label="" />
    );

  return (
    <div key="settings-button">
      <Tooltip
        content={label}
        hideTooltipOnClick
        isScreenReaderAnnouncementDisabled
      >
        <Button
          className={styles.button}
          onClick={onChange}
          aria-label={label}
          iconBefore={icon}
        />
      </Tooltip>
    </div>
  );
};
